import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LoadingProvider, useLoading } from './contexts/LoadingContext';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ScrollToTopOnRouteChange from './components/common/ScrollToTopOnRouteChange';
import PageLoader from './components/common/PageLoader';
import MarketingLayout from './layouts/MarketingLayout';
import AppLayout from './layouts/AppLayout';
import RequireAdmin from './components/admin/RequireAdmin';
import AdminLayout from './layouts/AdminLayout';

// HomePage is eagerly loaded - it's the landing page and we want fast first paint.
import HomePage from './pages/HomePage';

// All other pages are split into their own chunks so non-essential code is
// fetched only when the user actually navigates there.
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const AIAnalysisPage = lazy(() => import('./pages/AIAnalysisPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const TravelStylingPage = lazy(() => import('./pages/TravelStylingPage'));
const EventStylingPage = lazy(() => import('./pages/EventStylingPage'));
const PersonalStylingPage = lazy(() => import('./pages/PersonalStylingPage'));
const PersonalShoppingPage = lazy(() => import('./pages/PersonalShoppingPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const ShippingPolicyPage = lazy(() => import('./pages/ShippingPolicyPage'));
const ReturnsRefundsPage = lazy(() => import('./pages/ReturnsRefundsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const SharedReportPage = lazy(() => import('./pages/SharedReportPage'));

// Admin pages - only ever loaded for admin sessions
const AdminOverviewPage = lazy(() => import('./pages/admin/AdminOverviewPage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminAIResultsPage = lazy(() => import('./pages/admin/AdminAIResultsPage'));
const AdminCouponsPage = lazy(() => import('./pages/admin/AdminCouponsPage'));
const AdminTestimonialsPage = lazy(() => import('./pages/admin/AdminTestimonialsPage'));
const AdminPartnershipsPage = lazy(() => import('./pages/admin/AdminPartnershipsPage'));
const AdminAppOpsPage = lazy(() => import('./pages/admin/AdminAppOpsPage'));
const AdminContactPage = lazy(() => import('./pages/admin/AdminContactPage'));
const AdminCareersPage = lazy(() => import('./pages/admin/AdminCareersPage'));

// In-app pages - only loaded after sign-in
const DashboardPage = lazy(() => import('./pages/app/DashboardPage'));
const AppLoginPage = lazy(() => import('./pages/app/AppLoginPage'));
const TripCreatePage = lazy(() => import('./pages/app/TripCreatePage'));
const TripDetailsPage = lazy(() => import('./pages/app/TripDetailsPage'));
const ReportPage = lazy(() => import('./pages/app/ReportPage'));
const ProfilePage = lazy(() => import('./pages/app/ProfilePage'));
const CompleteProfilePage = lazy(() => import('./pages/app/CompleteProfilePage'));
const AppServicesPage = lazy(() => import('./pages/app/AppServicesPage'));
const ExpertRequestPage = lazy(() => import('./pages/app/ExpertRequestPage'));
const InvoicePage = lazy(() => import('./pages/app/InvoicePage'));
const MyPlansPage = lazy(() => import('./pages/app/MyPlansPage'));
const MyReportsPage = lazy(() => import('./pages/app/MyReportsPage'));
const OrdersPage = lazy(() => import('./pages/app/OrdersPage'));
const ShoppingBookingsPage = lazy(() => import('./pages/app/ShoppingBookingsPage'));
const ShoppingBookingNewPage = lazy(() => import('./pages/app/ShoppingBookingNewPage'));
const ShoppingBookingDetailPage = lazy(() => import('./pages/app/ShoppingBookingDetailPage'));
const AdminShoppingPage = lazy(() => import('./pages/admin/AdminShoppingPage'));
const AdminMoodboardPage = lazy(() => import('./pages/admin/AdminMoodboardPage'));
const AdminReportsPage = lazy(() => import('./pages/admin/AdminReportsPage'));
const AdminExpertRequestsPage = lazy(() => import('./pages/admin/AdminExpertRequestsPage'));
const AdminAppUsersPage = lazy(() => import('./pages/admin/AdminAppUsersPage'));

const RouteFallback: React.FC = () => <PageLoader isLoading />;

const AppContent: React.FC = () => {
  const { isLoading } = useLoading();

  return (
    <div className="min-h-screen">
      <PageLoader isLoading={isLoading} />
      <ScrollToTopOnRouteChange />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route element={<MarketingLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/ai-analysis" element={<AIAnalysisPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/travel-styling" element={<TravelStylingPage />} />
            <Route path="/event-styling" element={<EventStylingPage />} />
            <Route path="/personal-styling" element={<PersonalStylingPage />} />
            <Route path="/personal-shopping" element={<PersonalShoppingPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/r/:token" element={<SharedReportPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/shipping" element={<ShippingPolicyPage />} />
            <Route path="/returns" element={<ReturnsRefundsPage />} />
            {/* Friendly redirects for commonly-typed paths */}
            <Route path="/pricing" element={<Navigate to="/services" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>

          <Route
            path="/admin/*"
            element={
              <RequireAdmin>
                <AdminLayout />
              </RequireAdmin>
            }
          >
            <Route index element={<Navigate to="/admin/overview" replace />} />
            <Route path="overview" element={<AdminOverviewPage />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="ai-results" element={<AdminAIResultsPage />} />
            <Route path="coupons" element={<AdminCouponsPage />} />
            <Route path="testimonials" element={<AdminTestimonialsPage />} />
            <Route path="partnerships" element={<AdminPartnershipsPage />} />
            <Route path="careers" element={<AdminCareersPage />} />
            <Route path="shopping" element={<AdminShoppingPage />} />
            <Route path="moodboard" element={<AdminMoodboardPage />} />
            <Route path="reports" element={<AdminReportsPage />} />
            <Route path="expert-requests" element={<AdminExpertRequestsPage />} />
            <Route path="app-users" element={<AdminAppUsersPage />} />
            <Route path="contact" element={<AdminContactPage />} />
            <Route path="app-ops/*" element={<AdminAppOpsPage />} />
          </Route>

          <Route path="/app" element={<AppLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="dashboard" element={<Navigate to="/app" replace />} />
            <Route path="login" element={<AppLoginPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="complete-profile" element={<CompleteProfilePage />} />
            <Route path="ai-analysis" element={<AIAnalysisPage />} />
            <Route path="services" element={<AppServicesPage />} />
            <Route path="plans" element={<MyPlansPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="invoices">
              <Route path=":orderDocId" element={<InvoicePage />} />
            </Route>
            <Route path="expert">
              <Route path=":requestId" element={<ExpertRequestPage />} />
            </Route>
            <Route path="reports">
              <Route index element={<MyReportsPage />} />
              <Route path=":reportId" element={<ReportPage />} />
            </Route>
            <Route path="trips">
              <Route path="new" element={<TripCreatePage />} />
              <Route path=":tripId" element={<TripDetailsPage />} />
            </Route>
            <Route path="shopping">
              <Route index element={<ShoppingBookingsPage />} />
              <Route path="new" element={<ShoppingBookingNewPage />} />
              <Route path=":id" element={<ShoppingBookingDetailPage />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <Router>
          <AuthProvider>
            <LoadingProvider>
              <AppContent />
            </LoadingProvider>
          </AuthProvider>
        </Router>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
